/* eslint-disable no-nested-ternary */

import { ReactNode, useEffect, useState, useMemo } from 'react';

import { useLocation } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';

import chartIcon from 'assets/images/chart.svg';
import docIcon from 'assets/images/doc.svg';
import { SVGAsset } from 'components/atoms/SVGAsset';
import { LightboxGallery } from 'components/organisms/LightboxGallery';
import { LoaderScreen } from 'components/pages/LoaderScreen';
import { FadeAnimation } from 'components/templates/FadeWrapper';
import useElementSize from 'hooks/utils/useElementSize';
import { PageList } from 'types/api/pageList';

import { Header } from './Header';
import { MenuTree } from './MenuTree';
import {
    CategoryButton,
    CategoryButtonWrapper,
    PageLayoutArticle,
    ModalMenuBackground,
    PageLayoutMenu,
    PageLayoutWrapper,
    ModalMenuButtonWrapper,
    ModalMenuButton,
    MenuButtonWrapper,
    MenuButton,
} from './PageLayout.styled';

type PageLayoutProps = {
    children?: ReactNode;
    pages: PageList[];
    metaLoading: boolean;
    metaError: boolean;
    pageConfigLen: number | undefined;
};

export const PageLayout = ({
    pages,
    children,
    metaLoading,
    metaError,
    pageConfigLen,
}: PageLayoutProps): JSX.Element => {
    const [scrollPos, setScrollPos] = useState(window.pageYOffset);

    const handleScroll = (): void => {
        setScrollPos(window.pageYOffset);
    };

    const [modalMenu, setModalMenu] = useState<boolean>(false);

    const fundamentalPages = pages.filter((page) => page.is_fundamental);

    const [activeFundamentalPage, setActiveFundamentalPage] = useState<
        PageList['uuid']
    >(fundamentalPages[0].uuid);
    const handleCategoryClick = (cat: PageList['uuid']): void => {
        setActiveFundamentalPage(cat);
    };

    const { pathname } = useLocation();

    useEffect(() => {
        const currentPageByPath = pages.find((el) => el.href === pathname);
        if (currentPageByPath) {
            const currentCatByPage = currentPageByPath.href.split('/')[1];

            // set current category when page reloading
            if (currentCatByPage === 'pokazateli') {
                setActiveFundamentalPage(fundamentalPages[1].uuid);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pagesByCategory = useMemo(() => {
        const rootCategories = [
            ...pages.filter(
                (item) => item.route_uuid === activeFundamentalPage
            ),
        ];

        const subs = [rootCategories, [], [], []];

        subs.forEach((subItem, i) => {
            subItem.forEach((subPage) => {
                pages.forEach((page) => {
                    if (page.route_uuid === subPage.uuid) {
                        subs[i + 1].push(page);
                    }
                });
            });
        });

        return subs.flat();
    }, [activeFundamentalPage, pages]);

    useEffect(() => {
        handleScroll();

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const phone = useBreakpoint(down('lg'));

    useEffect(() => {
        setModalMenu(false);
    }, [phone]);

    const [headerRef, size] = useElementSize<HTMLDivElement>();

    useEffect(() => {
        if (modalMenu) {
            document.body.classList.add('modal-open');
        }
        return () => document.body.classList.remove('modal-open');
    }, [modalMenu]);

    const headerTopMargin = 20;

    return (
        <FadeAnimation>
            <Header ref={headerRef} />
            <PageLayoutWrapper>
                {phone ? (
                    <ModalMenuBackground modalMenu={modalMenu} />
                ) : undefined}
                <PageLayoutMenu modalMenu={modalMenu}>
                    <nav
                        style={
                            !phone
                                ? {
                                      top:
                                          scrollPos >
                                          size.height + headerTopMargin
                                              ? '32px'
                                              : `${
                                                    size.height -
                                                    scrollPos +
                                                    32 +
                                                    headerTopMargin
                                                }px`,
                                  }
                                : undefined
                        }
                        className="main_menu"
                        id="main_menu"
                    >
                        <MenuButtonWrapper>
                            <MenuButton onClick={() => setModalMenu(false)}>
                                <span>Меню</span>
                                <SVGAsset src="icons.24px.closeX" />
                            </MenuButton>
                        </MenuButtonWrapper>
                        <CategoryButtonWrapper>
                            {fundamentalPages.map((item, idx) => (
                                <CategoryButton
                                    key={item.id}
                                    className={
                                        activeFundamentalPage === item.uuid
                                            ? 'active'
                                            : ''
                                    }
                                    onClick={() =>
                                        handleCategoryClick(item.uuid)
                                    }
                                >
                                    {idx === 0 ? (
                                        <ReactSVG
                                            src={docIcon}
                                            wrapper="span"
                                        />
                                    ) : (
                                        <ReactSVG
                                            src={chartIcon}
                                            wrapper="span"
                                        />
                                    )}

                                    {item.title}
                                </CategoryButton>
                            ))}
                        </CategoryButtonWrapper>

                        <MenuTree
                            loading={metaLoading}
                            error={metaError}
                            pages={pagesByCategory}
                            onModalPageSelect={(show) => setModalMenu(show)}
                        />
                    </nav>
                </PageLayoutMenu>
                {!pageConfigLen ? (
                    <PageLayoutArticle>
                        <LoaderScreen positionStatic />
                    </PageLayoutArticle>
                ) : (
                    <PageLayoutArticle>{children}</PageLayoutArticle>
                )}

                <ModalMenuButtonWrapper
                    modalMenu={modalMenu}
                    onClick={() => setModalMenu(true)}
                >
                    <ModalMenuButton> Меню </ModalMenuButton>
                </ModalMenuButtonWrapper>
                <LightboxGallery />
            </PageLayoutWrapper>
        </FadeAnimation>
    );
};
